/**
 * PresentationStage — AI Empire Workshop main presentation surface.
 *
 * Attendees see the host's current slide (+ Expand). Host also gets
 * multi-slide upload, Previous / Next / Clear, and a thumbnail strip
 * when `canHost` is true. State comes from props (GET room + WS), so the
 * stage and the expanded viewer always show the live active slide.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSlideUploadQueue } from "./useSlideUploadQueue";
import { ExpandedSlideViewer } from "./ExpandedSlideViewer";
import { HostSlideStrip } from "./HostSlideStrip";
import { optimizeImage } from "@/lib/mediaUrls";

export const PresentationStage = ({
  roomId,
  presentationState,
  canHost,
  roomTitle,
}) => {
  const slides = presentationState?.slides || [];
  const activeId = presentationState?.active_slide_id || null;
  const active = slides.find((s) => s.slide_id === activeId) || null;
  const activeIdx = active ? slides.findIndex((s) => s.slide_id === activeId) : -1;
  const [pending, setPending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const fileRef = React.useRef(null);
  const queue = useSlideUploadQueue(roomId);

  const post = async (path, body, errMsg) => {
    if (pending) return;
    setPending(true);
    try {
      await api.post(path, body);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || errMsg);
      return false;
    } finally {
      setPending(false);
    }
  };
  const advance = (direction) => post(`/live/rooms/${roomId}/presentation/advance`, { direction }, "Could not advance slide");
  const jumpTo = (slide_id) => post(`/live/rooms/${roomId}/presentation/advance`, { slide_id }, "Could not show slide");
  const clearDeck = async () => {
    if (await post(`/live/rooms/${roomId}/presentation/clear`, {}, "Could not clear deck")) toast.success("Presentation cleared");
  };

  const onPick = (e) => {
    const { rejected } = queue.addFiles(e.target.files);
    rejected.forEach((r) => toast.error(`${r.name}: ${r.error}`));
    if (fileRef.current) fileRef.current.value = "";
  };

  const uploading = queue.running;
  const progressText = queue.currentIdx >= 0 ? `Uploading slide ${queue.currentIdx + 1} of ${queue.total}` : null;

  return (
    <div
      data-testid="workshop-presentation-stage"
      className="w-full rounded-2xl overflow-hidden border border-purple-500/30 bg-black/60 shadow-lg"
    >
      {/* 16:9 stage region — tap to expand */}
      <div
        className="relative w-full bg-gradient-to-br from-purple-900/60 to-black"
        style={{ aspectRatio: "16 / 9" }}
      >
        {active ? (
          <img
            key={active.slide_id}
            src={optimizeImage(active.image_url, 'cover')}
            alt={active.caption || "Workshop slide"}
            className="absolute inset-0 w-full h-full object-contain bg-black cursor-zoom-in"
            onClick={() => setExpanded(true)}
            data-testid="workshop-active-slide"
          />
        ) : (
          <div
            data-testid="workshop-stage-waiting"
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-6"
          >
            <div className="text-xs tracking-[0.3em] text-purple-300/70">
              AI EMPIRE WORKSHOP
            </div>
            <div className="text-xl sm:text-2xl font-bold text-white">
              {roomTitle || "Workshop starting soon"}
            </div>
            <div className="text-sm text-purple-200/60">
              {slides.length === 0
                ? (canHost ? "Upload your slides to begin" : "Waiting for the host…")
                : "Host will advance the first slide shortly"}
            </div>
          </div>
        )}
      </div>

      {/* Caption + progress strip + Expand (everyone) */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-black/70 border-t border-white/10">
        <div className="text-xs text-pink-200/80 truncate">
          {active?.caption || (slides.length > 0 ? `Slide ${activeIdx + 1} of ${slides.length}` : " ")}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {slides.length > 0 && (
            <div className="text-[10px] text-white/50" data-testid="workshop-slide-progress">
              {activeIdx >= 0 ? `${activeIdx + 1}/${slides.length}` : `0/${slides.length}`}
            </div>
          )}
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white"
            data-testid="workshop-expand-btn"
          >
            ⤢ Expand Presentation
          </button>
        </div>
      </div>

      {/* Host-only controls — never rendered for attendees */}
      {canHost && (
        <div data-testid="workshop-host-panel">
          <div
            data-testid="workshop-host-controls"
            className="flex flex-wrap items-center gap-2 px-3 py-2 bg-black/80 border-t border-purple-500/30"
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/*"
              multiple
              className="hidden"
              onChange={onPick}
              data-testid="workshop-upload-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="text-xs font-bold px-3 py-1.5 rounded-full bg-fuchsia-500/80 hover:bg-fuchsia-500 text-white disabled:opacity-50"
              data-testid="workshop-upload-slide-btn"
            >
              {uploading ? "Uploading…" : "＋ Upload Slides"}
            </button>
            <button
              type="button"
              onClick={() => advance("prev")}
              disabled={pending || slides.length === 0}
              className="text-xs font-bold px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-40"
              data-testid="workshop-prev-slide-btn"
            >
              ← Previous
            </button>
            <button
              type="button"
              onClick={() => advance("next")}
              disabled={pending || slides.length === 0}
              className="text-xs font-bold px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-40"
              data-testid="workshop-next-slide-btn"
            >
              Next →
            </button>
            <button
              type="button"
              onClick={clearDeck}
              disabled={pending || slides.length === 0 || uploading}
              className="ml-auto text-xs font-bold px-3 py-1.5 rounded-full bg-rose-500/30 hover:bg-rose-500/50 text-rose-100 border border-rose-400/40 disabled:opacity-40"
              data-testid="workshop-clear-deck-btn"
            >
              Clear
            </button>
          </div>
          {(progressText || queue.failed) && (
            <div className="px-3 py-2 bg-black/80 border-t border-white/10 text-xs" data-testid="workshop-upload-status">
              {progressText && <p className="text-fuchsia-200" data-testid="workshop-upload-progress">{progressText}</p>}
              {queue.failed && (
                <div className="flex flex-wrap items-center gap-2 text-rose-200" data-testid="workshop-upload-failed">
                  <span className="flex-1 min-w-0">
                    Slide {queue.items.indexOf(queue.failed) + 1} of {queue.total} ({queue.failed.name}) failed: {queue.failed.error}
                  </span>
                  <button type="button" onClick={queue.retry}
                    className="px-2.5 py-1 rounded-full bg-fuchsia-500/80 text-white font-bold"
                    data-testid="workshop-upload-retry">Retry</button>
                  <button type="button" onClick={queue.skip}
                    className="px-2.5 py-1 rounded-full bg-white/10 text-white font-bold"
                    data-testid="workshop-upload-skip">Skip</button>
                </div>
              )}
            </div>
          )}
          <HostSlideStrip slides={slides} activeIdx={activeIdx} onJump={jumpTo} pending={pending} />
        </div>
      )}

      {expanded && (
        <ExpandedSlideViewer
          slide={active}
          index={activeIdx}
          total={slides.length}
          canHost={canHost}
          pending={pending}
          onPrev={() => advance("prev")}
          onNext={() => advance("next")}
          onClose={() => setExpanded(false)}
        />
      )}
    </div>
  );
};

export default PresentationStage;
